/**
 * FIELD ONBOARDING, ON A SCREEN OF ITS OWN.
 *
 *   "you can put it back, but just remove the Field Onboarding part from it,
 *    because that has its own screen now."
 *    -- the founder, 13 September 2026
 *
 * It began inside the Human Capital card, was lifted out to stand above the
 * grid — "that's going to be our focus for the next few months so it must be
 * easily findable" — and now has an address. Three placements, each one the
 * founder's, and this file is the third.
 *
 * ---------------------------------------------------------------------------
 * THE COLLAPSE IS GONE, AND THAT IS NOT AN OMISSION
 *
 * The panel could be folded away because it sat on top of something else that
 * somebody might want to reach: "make the field onboarding section an
 * expandable and collapsible thing." There is nothing underneath it now. A
 * control whose whole purpose was to get this out of the way of the card grid
 * would, on a screen that is only this, collapse the page to a heading.
 *
 * The ruling it was balanced against is recorded in Portal.jsx, where the grid
 * it used to sit above now lives again.
 *
 * ---------------------------------------------------------------------------
 * WHO SEES IT: WHOEVER HOLDS EITHER HALF, AND NOBODY ELSE
 *
 * The gate is `permissionsThatStandAlone()` — the same function lib/rail.js
 * asks when it decides whether to draw the rail entry, not a second derivation
 * of it. Two copies of this question is how a rail entry ends up in front of
 * somebody the screen then refuses, which is a door onto nothing.
 *
 * AND THE GATE IS STILL A PERMISSION, NEVER A CARD. Prohibition 5, and the
 * reason fn_my_permissions had to exist at all: a field agent may hold nothing
 * that opens a single card, and their capability must still be on the screen.
 */

import { useEffect, useState } from 'react';
import { O, SANS, Card, LoadingPanel } from '../ui';
import { fetchMyPermissions } from '../lib/portal';
import { permissionsThatStandAlone } from '../lib/rail';
import { Page, PortalHeader } from '../Portal';
import FieldOnboarding from './FieldOnboarding';

export default function FieldOnboardingScreen({ staff, onSignOut, permissions = null }) {
  // PERMISSIONS ARRIVE AS A PROP WHEN THE SHELL HAS THEM, and are fetched here
  // when they do not. The shell asks once for the rail and hands them down;
  // this fallback exists so the screen is correct when mounted on its own,
  // rather than silently rendering a gate against `null`.
  const [own, setOwn] = useState(permissions);
  useEffect(() => {
    if (permissions !== null) { setOwn(permissions); return undefined; }
    let alive = true;
    fetchMyPermissions().then(({ data }) => { if (alive) setOwn(data || []); });
    return () => { alive = false; };
  }, [permissions]);

  if (own === null) {
    return (
      <Page wide>
        <PortalHeader staff={staff} onSignOut={onSignOut} />
        <LoadingPanel message="Checking what this account holds…" />
      </Page>
    );
  }

  const opensIt = permissionsThatStandAlone();
  const held = (own || []).some((p) => opensIt.includes(p.permission_key));

  return (
    <Page wide>
      <PortalHeader staff={staff} onSignOut={onSignOut} />
      <section aria-labelledby="field-onboarding-heading" data-testid="standing-field-onboarding">
        <h1
          id="field-onboarding-heading"
          style={{ fontFamily: SANS, fontSize: 17, fontWeight: 700, color: O.ink, margin: '0 0 6px' }}
        >
          Field Onboarding
        </h1>
        {held ? (
          <FieldOnboarding />
        ) : (
          // NOT AN ERROR, AND NOT AN EMPTY PAGE. Somebody who typed the address
          // or kept a bookmark after a grant was withdrawn gets a sentence, not
          // a blank screen and not a fault.
          <Card>
            <p style={{ fontSize: 14, color: O.ink }}>
              This account does not hold field onboarding.
            </p>
            <p style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
              It is granted from People &amp; Access. Your own modules are in the menu
              on the left.
            </p>
          </Card>
        )}
      </section>
    </Page>
  );
}
